import { priceTypingProcess, priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import {
    applyEquipmentTypeFieldVisibility,
    buildPayableInvoiceAmountField,
    buildPayableInvoiceDateFields,
    buildPayableInvoiceShopInfoField,
    invoiceEquipmentTypeOnChange,
    setFormFieldVisibility,
    TRAILER_FIELD,
    trailerSuggestionsLink,
    TRUCK_FIELD,
    validateInvoiceEquipment,
} from './payableInvoiceFormFields';

import { createIssueMoneyCodePostSuccessAction } from './issueMoneyCodeIssuedPopup';

export const ISSUE_FORM_NAME = 'issueMoneyCodeForm';

const TRUCK_PURPOSES = new Set(['Fuel', 'Scale', 'Accessories']);
const COMMON_MONEY_CODE_PURPOSES = new Set([
    'CashAdvance',
    'Fuel',
    'Scale',
    'LumperFee',
    'Accessories',
    'Parking',
    'Other',
]);

export const ISSUE_MONEY_CODE_PURPOSE_OPTIONS = [
    { value: '', label: '' },
    { value: 'NewInvoice', label: 'New Invoice' },
    { value: 'ExistingInvoice', label: 'Existing Invoice' },
    { value: 'CashAdvance', label: 'Cash Advance' },
    { value: 'Fuel', label: 'Fuel' },
    { value: 'Scale', label: 'Scale' },
    { value: 'LumperFee', label: 'Lumper Fee' },
    { value: 'Accessories', label: 'Accessories' },
    { value: 'Parking', label: 'Parking' },
    { value: 'Other', label: 'Other' },
];

const paperworkColumn = {
    label: 'Paperwork',
    name: 'paperwork',
    type: 'dropBox',
    fileType: 'any',
    file: null,
    ignoreAsRegularField: true,
    customDisplayMode: 'none',
    disabled: true,
};

/** Shared by New Invoice (cross-company) and Parking (current company only). */
const invoiceTrailerColumn = {
    ...TRAILER_FIELD,
    name: 'invoiceTrailer',
    mandatory: true,
    customDisplayMode: 'none',
    disabled: true,
};

function parseOptionalMoneyAmount(val) {
    if (val == null || val === '') return null;
    const parsed = parseFloat(String(val).replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
}

const DEFAULT_MONEY_CODE_AMOUNT_PLACEHOLDER = '$0.00';

const moneyCodeAmountColumn = {
    label: 'Money Code Amount (Optional)',
    name: 'moneyCodeAmount',
    placeholder: DEFAULT_MONEY_CODE_AMOUNT_PLACEHOLDER,
    mandatory: false,
    autoComplete: 'off',
    customDisplayMode: 'none',
    disabled: true,
    filters: [priceTypingProcess],
    onblur: (val) => priceOnFinish(val),
    defaultValue: () => '',
    processBeforeSending: parseOptionalMoneyAmount,
};

function setMoneyCodeAmountPlaceholder(formTarget, invoiceAmount) {
    const amountInput = formTarget?.querySelector?.('input[name="moneyCodeAmount"]');
    const placeholder =
        invoiceAmount != null && Number.isFinite(Number(invoiceAmount))
            ? priceOnFinish(invoiceAmount)
            : DEFAULT_MONEY_CODE_AMOUNT_PLACEHOLDER;
    moneyCodeAmountColumn.placeholder = placeholder;
    if (amountInput) {
        amountInput.placeholder = placeholder;
    }
}

function syncMoneyCodeAmountPlaceholderFromInvoice(rawValJson, formTarget) {
    if (!rawValJson) {
        setMoneyCodeAmountPlaceholder(formTarget, null);
        return true;
    }
    try {
        const invoice = typeof rawValJson === 'string' ? JSON.parse(rawValJson) : rawValJson;
        const unpaid =
            invoice?.unpaidAmount != null ? invoice.unpaidAmount : invoice?.amount;
        setMoneyCodeAmountPlaceholder(formTarget, unpaid);
    } catch {
        setMoneyCodeAmountPlaceholder(formTarget, null);
    }
    return true;
}

function syncMoneyCodeAmountField(formTarget, purpose) {
    const isExistingInvoice = purpose === 'ExistingInvoice';
    const isNewInvoice = purpose === 'NewInvoice';

    moneyCodeAmountColumn.mandatory = isExistingInvoice;
    moneyCodeAmountColumn.label = isNewInvoice
        ? 'Money Code Amount (Optional)'
        : 'Money Code Amount';

    const labelEl = formTarget.querySelector('#moneyCodeAmountLabel');
    if (labelEl) {
        labelEl.textContent = moneyCodeAmountColumn.label;
        if (isExistingInvoice) {
            const star = document.createElement('span');
            star.textContent = ' *';
            star.style.fontWeight = '900';
            star.style.color = 'var(--alert-color)';
            labelEl.appendChild(star);
        }
    }

    if (!isExistingInvoice) {
        setMoneyCodeAmountPlaceholder(formTarget, null);
    } else {
        const invoiceInput = formTarget.querySelector('input[name="invoice"]');
        const raw = invoiceInput?.getAttribute('data-value');
        if (raw) {
            syncMoneyCodeAmountPlaceholderFromInvoice(raw, formTarget);
        } else {
            setMoneyCodeAmountPlaceholder(formTarget, null);
        }
    }
}

function purposeSelectionOnChange(event) {
    let formTarget = event.target;
    while (formTarget && formTarget.tagName !== 'FORM') {
        formTarget = formTarget.parentNode;
    }
    if (!formTarget) return;
    const purpose = event.target.value;
    if (purpose !== 'NewInvoice' && paperworkColumn.file) {
        paperworkColumn.file = null;
    }
    applyPurposeFieldVisibility(formTarget, purpose);
}

export function applyPurposeFieldVisibility(formTarget, purpose) {
    const showDriver = purpose === 'CashAdvance';
    const showLoad = purpose === 'LumperFee';
    const showTruckRef = TRUCK_PURPOSES.has(purpose);
    const showParking = purpose === 'Parking';
    const showCommon = COMMON_MONEY_CODE_PURPOSES.has(purpose);
    const showNewInvoice = purpose === 'NewInvoice';
    const showExistingInvoice = purpose === 'ExistingInvoice';

    setFormFieldVisibility(formTarget, 'driver', showDriver);
    setFormFieldVisibility(formTarget, 'load', showLoad);
    setFormFieldVisibility(formTarget, 'reimbursed', showDriver || showLoad);
    setFormFieldVisibility(formTarget, 'invoice', showExistingInvoice);
    setFormFieldVisibility(formTarget, 'moneyCodeAmount', showNewInvoice || showExistingInvoice);
    setFormFieldVisibility(formTarget, 'amount', showCommon);
    setFormFieldVisibility(formTarget, 'invoiceAmount', showNewInvoice);
    setFormFieldVisibility(formTarget, 'description', showCommon || showNewInvoice);
    setFormFieldVisibility(formTarget, 'equipmentType', showNewInvoice || showParking);
    setFormFieldVisibility(formTarget, 'createdDate', showNewInvoice);
    setFormFieldVisibility(formTarget, 'shopName', showNewInvoice);
    setFormFieldVisibility(formTarget, 'dueDate', showNewInvoice);
    setFormFieldVisibility(formTarget, 'paperwork', showNewInvoice);

    setFormFieldVisibility(formTarget, 'truck', showTruckRef);
    setFormFieldVisibility(formTarget, 'invoiceTruck', false);
    setFormFieldVisibility(formTarget, 'invoiceTrailer', false);

    // New Invoice: sister-company trailers OK; Parking: current company (+ third-party) only.
    invoiceTrailerColumn.suggestionsLink = trailerSuggestionsLink(showNewInvoice);

    if (showNewInvoice || showParking) {
        const equipmentSelect = formTarget.querySelector('select[name="equipmentType"]');
        if (showParking && equipmentSelect && !equipmentSelect.value) {
            equipmentSelect.value = 'Truck';
        }
        const equipmentType = equipmentSelect?.value || (showParking ? 'Truck' : '');
        applyEquipmentTypeFieldVisibility(formTarget, equipmentType, {
            truckField: 'invoiceTruck',
            trailerField: 'invoiceTrailer',
        });
    }

    syncMoneyCodeAmountField(formTarget, purpose);
}

function buildMoneyCodeAmountField() {
    return {
        label: 'Amount',
        name: 'amount',
        mandatory: true,
        placeholder: '$0.00',
        autoComplete: 'off',
        customDisplayMode: 'none',
        disabled: true,
        filters: [priceTypingProcess],
        onblur: (val) => priceOnFinish(val),
        defaultValue: () => '',
        processBeforeSending: (val) => parseFloat(String(val).replace(/[^0-9.]/g, '')),
    };
}

function buildInvoiceAmountField() {
    return {
        ...buildPayableInvoiceAmountField({ hiddenInitially: true }),
        name: 'invoiceAmount',
        label: 'Invoice Amount',
    };
}

function buildMoneyCodeDescriptionField() {
    return {
        label: 'Description',
        name: 'description',
        type: 'textArea',
        rowCount: 4,
        customDisplayMode: 'none',
        disabled: true,
        defaultValue: () => '',
    };
}

function buildNewInvoicePayload(data) {
    return {
        equipmentType: data.equipmentType,
        amount: data.invoiceAmount,
        createdDate: data.createdDate,
        shopName: data.shopName != null ? String(data.shopName).trim() : '',
        dueDate: data.dueDate,
        description: data.description || null,
        truck: data.equipmentType === 'Truck' ? data.invoiceTruck : null,
        trailer: data.equipmentType === 'Trailer' ? data.invoiceTrailer : null,
    };
}

function buildIssueMoneyCodeRequestBody(data, file) {
    const purposeSelection = data.purposeSelection;
    let payload;

    if (purposeSelection === 'NewInvoice') {
        const moneyCode = { purpose: 'Invoice' };
        const codeAmount = parseOptionalMoneyAmount(data.moneyCodeAmount);
        if (codeAmount != null && codeAmount > 0) {
            moneyCode.amount = codeAmount;
        }
        payload = {
            moneyCode,
            newInvoice: buildNewInvoicePayload(data),
        };
    } else if (purposeSelection === 'ExistingInvoice') {
        payload = {
            moneyCode: {
                purpose: 'Invoice',
                invoice: data.invoice,
                amount: parseOptionalMoneyAmount(data.moneyCodeAmount),
            },
            newInvoice: null,
        };
    } else {
        const moneyCode = {
            purpose: purposeSelection,
            amount: data.amount,
        };
        if (data.description) {
            moneyCode.description = data.description;
        }
        if (data.driver) {
            moneyCode.driver = data.driver;
        }
        if (purposeSelection === 'Parking') {
            if (data.equipmentType === 'Truck' && data.invoiceTruck) {
                moneyCode.truck = data.invoiceTruck;
            } else if (data.equipmentType === 'Trailer' && data.invoiceTrailer) {
                moneyCode.trailer = data.invoiceTrailer;
            }
        } else if (data.truck) {
            moneyCode.truck = data.truck;
        }
        if (data.load) {
            moneyCode.load = data.load;
        }
        payload = { moneyCode, newInvoice: null };
    }

    const body = new FormData();
    body.append('issueData', JSON.stringify(payload));
    if (purposeSelection === 'NewInvoice' && file) {
        body.append('file', file);
        body.append('fileName', file.name);
        body.append('fileType', 'any');
    }
    return body;
}

async function validateNewInvoiceEquipment(data) {
    return validateInvoiceEquipment({
        equipmentType: data.equipmentType,
        truck: data.invoiceTruck,
        trailer: data.invoiceTrailer,
    });
}

async function validateIssueMoneyCodeForm(data) {
    const purpose = data.purposeSelection;
    if (!purpose) {
        addNotification({ message: 'Select a purpose', type: 'alert' });
        return { cancelSubmission: true };
    }
    if (purpose === 'NewInvoice') {
        if (!localStorage.getItem('orgId')) {
            addNotification({ message: 'Organization is missing; cannot create a new invoice', type: 'alert' });
            return { cancelSubmission: true };
        }
        const equipmentResult = await validateNewInvoiceEquipment(data);
        if (equipmentResult.cancelSubmission) {
            return equipmentResult;
        }
        const codeAmount = parseOptionalMoneyAmount(data.moneyCodeAmount);
        if (codeAmount != null && codeAmount <= 0) {
            addNotification({ message: 'Money code amount must be greater than zero', type: 'alert' });
            return { cancelSubmission: true };
        }
        return {};
    }
    if (purpose === 'ExistingInvoice') {
        if (!data.invoice) {
            addNotification({ message: 'Select an invoice', type: 'alert' });
            return { cancelSubmission: true };
        }
        const codeAmount = parseOptionalMoneyAmount(data.moneyCodeAmount);
        if (codeAmount == null || codeAmount <= 0) {
            addNotification({ message: 'Enter a money code amount', type: 'alert' });
            return { cancelSubmission: true };
        }
        return {};
    }
    if (purpose === 'CashAdvance' && !data.driver) {
        addNotification({ message: 'Select a driver', type: 'alert' });
        return { cancelSubmission: true };
    }
    if (TRUCK_PURPOSES.has(purpose) && !data.truck) {
        addNotification({ message: 'Select a truck', type: 'alert' });
        return { cancelSubmission: true };
    }
    if (purpose === 'Parking') {
        const equipmentResult = await validateInvoiceEquipment({
            equipmentType: data.equipmentType,
            truck: data.invoiceTruck,
            trailer: data.invoiceTrailer,
        });
        if (equipmentResult.cancelSubmission) {
            return equipmentResult;
        }
    }
    if (purpose === 'LumperFee' && !data.load) {
        addNotification({ message: 'Select a load', type: 'alert' });
        return { cancelSubmission: true };
    }
    if (purpose === 'Other' && (!data.description || !String(data.description).trim())) {
        addNotification({ message: 'Description is required', type: 'alert' });
        return { cancelSubmission: true };
    }
    return {};
}

export function buildIssueMoneyCodeForm({ onIssued } = {}) {
    const [createdDateField, dueDateField] = buildPayableInvoiceDateFields({ hiddenInitially: true });

    const configuration = {
        formTitle: 'Issue Money Code',
        customSubmitButton: 'Issue Code',
        name: ISSUE_FORM_NAME,
        customMethod: 'POST',
        addLink: API_ROOT + 'moneycodes/issue',
        successMessage: 'Money code was issued',
        EZCloseAllowed: false,
        buildRequestBody: buildIssueMoneyCodeRequestBody,
        // Omit Content-Type so the browser sets multipart boundary; companyId is injected at request time.
        customContentType: {},
        preSendManipulation: (body) => body,
        customCheckBeforeSending: validateIssueMoneyCodeForm,
        customUseEffect: () => {
            const form = document.getElementById(ISSUE_FORM_NAME);
            if (form) {
                applyPurposeFieldVisibility(form, '');
            }
        },
        columns: [
            {
                label: 'Purpose',
                name: 'purposeSelection',
                type: 'select',
                dataset: ISSUE_MONEY_CODE_PURPOSE_OPTIONS,
                mandatory: true,
                onChange: purposeSelectionOnChange,
            },
            {
                label: 'Driver',
                name: 'driver',
                suggestionsEnabled: true,
                suggestionsLink: API_ROOT + 'drivers/namecontaines?statusagnostic=true&name=',
                dataValueRequired: true,
                mandatory: true,
                autoComplete: 'off',
                customDisplayMode: 'none',
                disabled: true,
                displayValueGenerator: (rawValue) =>
                    [rawValue.firstName, rawValue.lastName].filter(Boolean).join(' ').trim(),
                afterMinimumChars: 2,
            },
            {
                label: 'Load',
                name: 'load',
                suggestionsEnabled: true,
                suggestionsLink: API_ROOT + 'loads/findLoadByLoadNumber?loadNumber=',
                dataValueRequired: true,
                mandatory: true,
                autoComplete: 'off',
                customDisplayMode: 'none',
                disabled: true,
                displayValueGenerator: (val) =>
                    `${val.loadNumber}${val.broker?.brokerageName ? ` (${val.broker.brokerageName})` : ''}`,
                afterMinimumChars: 2,
            },
            {
                label: 'Invoice #',
                name: 'invoice',
                suggestionsEnabled: true,
                suggestionsLink: API_ROOT + 'invoices/search?pattern=',
                dataValueRequired: true,
                mandatory: true,
                autoComplete: 'off',
                customDisplayMode: 'none',
                disabled: true,
                displayValueGenerator: (rawValue) =>
                    rawValue.invoiceNumber != null ? String(rawValue.invoiceNumber) : '',
                rawValComparationFieldName: 'invoiceNumber',
                afterMinimumChars: 1,
                afterSelection: (rawValJson) => {
                    const formTarget = document.getElementById(ISSUE_FORM_NAME);
                    return syncMoneyCodeAmountPlaceholderFromInvoice(rawValJson, formTarget);
                },
                onSuggestionCleared: (target) => {
                    const formTarget = target?.form || document.getElementById(ISSUE_FORM_NAME);
                    setMoneyCodeAmountPlaceholder(formTarget, null);
                },
                onblur: (val, target) => {
                    const formTarget = target?.form || document.getElementById(ISSUE_FORM_NAME);
                    if (!val) {
                        setMoneyCodeAmountPlaceholder(formTarget, null);
                    } else {
                        const raw = target?.getAttribute?.('data-value');
                        if (raw) {
                            syncMoneyCodeAmountPlaceholderFromInvoice(raw, formTarget);
                        }
                    }
                    return val;
                },
            },
            {
                label: 'Equipment type',
                name: 'equipmentType',
                type: 'select',
                dataset: ['', 'Truck', 'Trailer'],
                mandatory: true,
                customDisplayMode: 'none',
                disabled: true,
                onChange: invoiceEquipmentTypeOnChange,
            },
            {
                ...TRUCK_FIELD,
                label: 'Truck',
                name: 'invoiceTruck',
                mandatory: true,
                customDisplayMode: 'none',
                disabled: true,
            },
            invoiceTrailerColumn,
            {
                label: 'Truck',
                name: 'truck',
                suggestionsEnabled: true,
                suggestionsLink: API_ROOT + 'trucks/allTrucksBySimilarTruckNumber?pattern=',
                dataValueRequired: true,
                mandatory: true,
                autoComplete: 'off',
                customDisplayMode: 'none',
                disabled: true,
                displayValueGenerator: (rawValue) => `${rawValue.truckNumber}`,
                rawValComparationFieldName: 'truckNumber',
                afterMinimumChars: 2,
            },
            buildInvoiceAmountField(),
            moneyCodeAmountColumn,
            buildMoneyCodeAmountField(),
            createdDateField,
            buildPayableInvoiceShopInfoField({ hiddenInitially: true }),
            dueDateField,
            buildMoneyCodeDescriptionField(),
            paperworkColumn,
        ],
    };

    if (onIssued) {
        configuration.customPostSuccessAction = createIssueMoneyCodePostSuccessAction(onIssued);
    }

    return configuration;
}
